<script setup lang="ts">
// Copper fund page (AMATI Phase 2.5 lean, 2026-10-11): the WS Amati Strategic
// Metals Signal tab. AMATI strikes a BUY/SELL decision at 08:40 London off the
// COMEX copper (HG) move since the previous 16:00 New York close — the
// amati_mt_signal producer's latest:signal:amati payload, read verbatim via
// GET /api/signal?fund=amati (404 unless the caller may view AMATI).
//
// Deliberately lean (no decision clock, no NAV card — see
// docs/runbooks/amati-fund-dark.md "Fund page"): one calm verdict card plus
// the shared FuturesMoveCard. Since 2026-10-15 (owner approval) the shared live
// K_t panel sits under it — it renders nothing while Amati:KtLiveEnabled is off. A 404 / failed fetch / `available:false` /
// degraded payload all render the same calm "No live signal yet" state —
// never an alert, never a throw. `ctx` is accepted (uniform signalTab
// contract) and ignored — this tab polls its own data, like CharterisSignalTab.
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { api, type AmatiSignal, type GatedFuturesSignal } from '../../api/client'
import type { SignalTabCtx } from '../nav/fundPages'
import FuturesMoveCard from './FuturesMoveCard.vue'
import { formatSignedBp } from '../japan/japanReasons'
import LiveKtPanel from '../signal/LiveKtPanel.vue'
import { useAmatiLiveSynthNav } from '../../composables/useLiveSynthNav'

defineProps<{ ctx: SignalTabCtx }>()

const SIGNAL_POLL_MS = 60_000

const signal = ref<AmatiSignal | null>(null)

/** A usable live decision: present, not degraded, BUY or SELL. */
const live = computed<AmatiSignal | null>(() => {
  const s = signal.value
  if (!s || s.available === false || s.degraded === true) return null
  if (s.decision !== 'BUY' && s.decision !== 'SELL') return null
  return s
})

const LONDON_DATE = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
})
const LONDON_HHMM = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
})
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const todayLondon = computed(() => LONDON_DATE.format(new Date()))

const hgMoveLabel = computed(() => {
  const m = live.value?.m_0840
  return m == null ? '—' : `${formatSignedBp(m)} bp`
})

const asOfLabel = computed(() => {
  const ts = live.value?.timestamp
  if (!ts) return null
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return null
  const day = LONDON_DATE.format(d)
  const hhmm = `${LONDON_HHMM.format(d)} London`
  if (day === todayLondon.value) return `Published ${hhmm} today`
  const [, mo, dd] = day.split('-').map(Number)
  return `Published ${hhmm} on ${dd} ${MONTHS[mo - 1]}`
})

/** FuturesMoveCard's reason line — only ever called for a healthy payload. */
function describeAmatiMove(s: GatedFuturesSignal): { text: string; tone: 'ok' | 'amber' } {
  if (s.m_0840 == null) return { text: 'HG move unavailable', tone: 'ok' }
  const t = Math.round(s.threshold_bps ?? 0)
  const side = s.m_0840 * 10000 >= t ? 'at or above' : 'below'
  return { text: `HG ${formatSignedBp(s.m_0840)} bp since NY close — ${side} the ${t} bp threshold`, tone: 'ok' }
}

async function pollSignal() {
  try {
    signal.value = await api.amatiSignal()
  } catch {
    // 404 (not viewable / not yet published) or a network blip: calm empty state.
    signal.value = null
  }
}

let timer: ReturnType<typeof setInterval> | null = null
onMounted(() => {
  pollSignal()
  timer = setInterval(pollSignal, SIGNAL_POLL_MS)
})
onUnmounted(() => {
  if (timer) clearInterval(timer)
})

// Live K_t (owner approval 2026-10-15): /api/amati/synth-nav-live, polled every 5 s; null (404) while the switch is off.
const { data: liveKt } = useAmatiLiveSynthNav()
const KT_TIER_TITLE: Record<string, string> = {
  live: 'Gold, silver and copper futures bars under 3 minutes old',
  degraded: 'Metal futures bars 3–10 minutes old (or the market just reopened)',
  critical: 'Metal futures feeds stale while the market trades — estimate zeroed',
  closed: 'COMEX metals closed — last-close estimate',
}
const KT_NOTE = 'Betas uncalibrated (v1): gold/silver/copper names roll on GC/SI/HG at 1.0; nickel, lithium and graphite carry their last price; US quotes are 15-min delayed.'
</script>

<template>
  <div class="card amati-verdict" data-testid="amati-verdict">
    <div class="av-head">
      <span class="av-title">Copper · 08:40 London decision</span>
      <span class="av-sub">COMEX copper (HG) since the previous NY close</span>
    </div>
    <template v-if="live">
      <div class="av-row">
        <span class="av-decision" :class="live.decision === 'BUY' ? 'av-buy' : 'av-sell'" data-testid="amati-decision">
          {{ live.decision }}
        </span>
        <span class="av-move" data-testid="amati-hg-move">HG {{ hgMoveLabel }}</span>
      </div>
      <div v-if="asOfLabel" class="av-asof" data-testid="amati-asof">{{ asOfLabel }}</div>
    </template>
    <div v-else class="av-empty" data-testid="amati-no-signal">
      No live signal yet — the next decision is at 08:40 London
    </div>
  </div>
  <FuturesMoveCard
    v-if="live"
    :signal="live"
    :today-london="todayLondon"
    symbol="HG"
    anchor-label="NY close"
    test-id="hg-move-card"
    :describe="describeAmatiMove"
  />
  <!-- Live K_t (2026-10-15): the shared card + coverage bar + holdings table; nothing while the switch is off. -->
  <!-- The daily K_t book card was removed 2026-10-15: the live panel's holdings table is the one holdings list. -->
  <LiveKtPanel :live="liveKt" test-id-prefix="amati" :tier-titles="KT_TIER_TITLE" tag-label="Future" :note="KT_NOTE" />
</template>

<style scoped>
.amati-verdict { display: flex; flex-direction: column; gap: 6px; }
.av-head { display: flex; flex-direction: column; gap: 2px; }
.av-title { font-size: 0.9rem; font-weight: 600; color: #e6e6ef; }
.av-sub { font-size: 0.76rem; color: #8b949e; }
.av-row { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
.av-decision { font-size: 1.6rem; font-weight: 700; letter-spacing: 0.02em; }
.av-buy { color: #3fb950; }
.av-sell { color: #f85149; }
.av-move { font-size: 0.95rem; font-variant-numeric: tabular-nums; color: #c9d1d9; }
.av-asof { font-size: 0.78rem; color: #8b949e; }
.av-empty { font-size: 0.85rem; color: #8b949e; padding: 4px 0; }
</style>
